import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { clientPortalApi } from "./clientPortalApi";
import { getBtsSeenAt, countNewBts } from "./btsNudge";
import { useDocumentTitle } from "./useDocumentTitle";
import { ArrowRight, Camera } from "lucide-react";
import "./clientPortal.css";

const PREVIOUS_PREVIEW_COUNT = 4;

function fmtDay(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yest)) return "Yesterday";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Client Portal v2 (2026-09, Phase 1) Home — replaces the v1 PM-card-plus-grid layout. §2/§3
 * of the approved prompt: greeting, Active projects as calm rows (each carrying its OWN PM —
 * §7, no global PM card), a light "Recent NJM updates" feed aggregated client-side across
 * active projects (no new endpoint), and a Previous projects preview. "Needs your attention" is
 * intentionally omitted this phase (confirmed — derivation logic is Phase 2).
 *
 * Perf fix (2026-09, Client Portal audit finding): used to fire one `getUpdates(p.id)` call
 * PER active project via `Promise.all` after `/home` resolved (measured 32 parallel calls,
 * 1.3s, 139KB for a 32-active-project client) just to read each project's `items[0]`.
 * `/home` now returns `latest_update` inline per project (one batched query server-side) — no
 * extra network calls at all for "Latest: ..." row text or the Recent NJM updates feed. */
export default function ClientPortalHome() {
  useDocumentTitle("Home");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllPrevious, setShowAllPrevious] = useState(false);

  useEffect(() => {
    let mounted = true;
    clientPortalApi.home().then((r) => {
      if (!mounted) return;
      setData(r.data);
    }).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  // Previous projects bugfix (2026-06, Tessolve audit): "Previous" is now the server's
  // explicit `is_previous` (Delivered/Archived only — kernel/client_serializer.py's
  // CLIENT_PREVIOUS_STATUSES), never `!is_active`. The old derivation filed EVERY non-active
  // status under a client-facing "Previous projects" heading, so a staff project-scoped
  // preview of a Lead project showed that Lead as the client's only "previous project".
  // Anything neither active nor previous can ONLY be a staff project-scoped preview of a
  // pre-sale/hidden project (a real client session can never receive one) — it gets its own
  // preview card below, never client history.
  const { active, previous, previewOnly, recentUpdates } = useMemo(() => {
    const projects = data?.projects || [];
    const activeProjects = projects.filter((p) => p.is_active);
    const recent = activeProjects
      .filter((p) => p.latest_update)
      .map((p) => ({ ...p.latest_update, projectName: p.name, projectId: p.id }))
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
      .slice(0, 6);
    return {
      active: activeProjects,
      previous: projects.filter((p) => p.is_previous),
      previewOnly: projects.filter((p) => !p.is_active && !p.is_previous),
      recentUpdates: recent,
    };
  }, [data]);
  const visiblePrevious = showAllPrevious ? previous : previous.slice(0, PREVIOUS_PREVIEW_COUNT);

  if (loading) {
    return <div className="cp-theme min-h-[60vh] flex items-center justify-center text-[color:var(--cp-grey-3)] text-sm" data-testid="client-home-loading">Loading your workspace…</div>;
  }

  const firstName = (data?.name || "").split(" ")[0] || "there";

  return (
    <div className="cp-theme cp-enter max-w-[1200px] mx-auto px-4 md:px-10 py-9 md:py-12" data-testid="client-portal-home">
      <h1 className="cp-display text-[28px] md:text-[38px] text-[color:var(--cp-ink-text)]">Good {greeting()}, {firstName}.</h1>
      <p className="mt-1.5 text-[13.5px] text-[color:var(--cp-grey-3)]">Here's where your NJM projects stand today.</p>

      <section className="mt-10" data-testid="client-home-active-projects">
        <div className="cp-section-rule flex items-baseline justify-between pt-3">
          <span className="cp-eyebrow">Active projects</span>
          <Link to="/client-portal/projects" className="cp-btn-ghost inline-flex items-center gap-1" data-testid="client-home-see-all-link">See all projects <ArrowRight className="h-3 w-3" /></Link>
        </div>
        {active.length === 0 ? (
          <p className="py-10 text-sm text-[color:var(--cp-grey-3)]" data-testid="client-home-no-active">No active projects right now. Request one whenever you're ready.</p>
        ) : (
          <div>
            {active.map((p) => {
              const seenAt = getBtsSeenAt(p.id);
              const newBts = countNewBts(p.bts_timestamps, seenAt);
              const latest = p.latest_update;
              return (
                <Link key={p.id} to={`/client-portal/projects/${p.id}`} className="cp-row-divider group flex flex-col md:flex-row md:items-center gap-3 py-5 hover:bg-black/[.02] transition-colors -mx-2 px-2" data-testid={`client-home-project-row-${p.id}`}>
                  <div className="md:w-[280px] shrink-0">
                    <div className="flex items-center gap-2">
                      <span className="cp-status-dot" />
                      <span className="font-semibold text-[15px] text-[color:var(--cp-ink-text)]">{p.name}</span>
                    </div>
                    <div className="mt-1 text-[11px] font-semibold tracking-wide uppercase text-[color:var(--cp-grey-3)]">{p.status_label}{p.category ? ` · ${p.category}` : ""}</div>
                  </div>
                  <div className="flex-1 min-w-0 text-[13px] text-[color:var(--cp-grey-1)]">
                    {latest ? <span className="line-clamp-1"><span className="text-[color:var(--cp-grey-3)]">Latest: </span>{latest.body}</span> : <span className="text-[color:var(--cp-grey-4)]">No updates posted yet.</span>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {newBts > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-[color:var(--cp-gold-dim)]" data-testid={`client-home-bts-nudge-${p.id}`}>
                        <Camera className="h-3 w-3" /> +{newBts} from the shoot
                      </span>
                    )}
                    {p.pm && <span className="hidden lg:inline text-[11px] text-[color:var(--cp-grey-4)]">{p.pm.name}</span>}
                    <span className="cp-btn-outline px-3 py-1.5 shrink-0">Open project</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      <section className="mt-12" data-testid="client-home-recent-updates">
        <div className="cp-section-rule pt-3">
          <span className="cp-eyebrow">Recent NJM updates</span>
        </div>
        {recentUpdates.length === 0 ? (
          <p className="py-8 text-sm text-[color:var(--cp-grey-3)]">No updates yet. You'll see them here as your projects move.</p>
        ) : (
          <div>
            {recentUpdates.map((u) => (
              <div key={u.id} className="cp-row-divider py-4" data-testid={`client-home-update-${u.id}`}>
                <div className="flex items-baseline gap-2 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--cp-grey-4)]">
                  <span>{fmtDay(u.date)}</span><span>·</span><span>{u.projectName}</span>
                </div>
                <p className="mt-1.5 text-[13.5px] text-[color:var(--cp-ink-text)]">{u.body}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {previewOnly.length > 0 && (
        <section className="mt-12" data-testid="client-home-preview-only">
          <div className="cp-section-rule pt-3">
            <span className="cp-eyebrow">Staff preview</span>
          </div>
          <div>
            {previewOnly.map((p) => (
              <Link key={p.id} to={`/client-portal/projects/${p.id}`} className="cp-row-divider flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 py-3.5 hover:bg-black/[.02] transition-colors -mx-2 px-2" data-testid={`client-home-preview-row-${p.id}`}>
                <span className="text-[13.5px] text-[color:var(--cp-grey-1)]">{p.name}</span>
                <span className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--cp-gold-dim)]" data-testid={`client-home-preview-note-${p.id}`}>
                  Preview only · not visible to your client
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {previous.length > 0 && (
        <section className="mt-12 mb-4" data-testid="client-home-previous-projects">
          <div className="cp-section-rule pt-3">
            <span className="cp-eyebrow">Previous projects</span>
          </div>
          <div>
            {visiblePrevious.map((p) => (
              <Link key={p.id} to={`/client-portal/projects/${p.id}`} className="cp-row-divider flex items-center justify-between py-3.5 hover:bg-black/[.02] transition-colors -mx-2 px-2" data-testid={`client-home-previous-row-${p.id}`}>
                <span className="text-[13.5px] text-[color:var(--cp-grey-1)]">{p.name}</span>
                <span className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--cp-grey-4)]">{p.status_label}</span>
              </Link>
            ))}
          </div>
          {previous.length > PREVIOUS_PREVIEW_COUNT && (
            <button type="button" onClick={() => setShowAllPrevious((v) => !v)} data-testid="client-home-previous-see-all"
              className="cp-btn-ghost mt-3 inline-flex items-center gap-1">
              {showAllPrevious ? "Show fewer" : `See all (${previous.length})`}
            </button>
          )}
        </section>
      )}
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}
